<script setup generic="T" lang="ts">
// TODO провести рефакторинг
import './style.scss';

import { UiIcon, UiBtn } from '@shared/ui';
import type { ModelRef } from 'vue';
import type { UiInputProps, UiInputEmits } from './types';

const className = 'ui-input';

const model = defineModel<T>({ required: false }) as ModelRef<string>;

const {
  type = 'text',
  name = '',
  placeholder,
  label = '',
  icon,
  focus = false,
  info,
  error,
  inputClass,
  success,
  oneWord = false,
  lowerCase = false,
  onlyNum = false,
  maxLength,
  disabled = false,
  mask,
  description,
  suffix,
  prefix,
  readonly = false,
  required = false,
} = defineProps<UiInputProps>();

const emit = defineEmits<UiInputEmits>();

const inputRef = ref<HTMLInputElement>();
const isShowPassword = ref(false);
const uid = useId();

const clear = () => {
  if (!readonly) {
    model.value = '';
  }
  emit('onClear', model.value);
};

const onInput = (e: Event) => {
  if (lowerCase) {
    model.value = model.value?.toLowerCase();
  }

  if (oneWord) {
    model.value = model.value?.trim();
  }

  if (onlyNum) {
    model.value = model.value?.replace(/\D/g, '');
  }

  if (maxLength) {
    model.value = model.value?.slice(0, maxLength);
  }

  if (mask === 'phone') {
    const event = e as Event & InputEvent;
    const input = inputRef.value as HTMLInputElement;
    const selectionStart = input.selectionStart;

    let numericValue = input.value.replace(/\D/g, '');
    let formattedValue = '+7 (';

    if (!numericValue) {
      return (input.value = formattedValue);
    }

    if (input.value.length !== selectionStart) {
      if (event.data && /\D/g.test(event.data)) {
        input.value = numericValue;
      }
      return;
    }

    if (numericValue?.length === 1 && numericValue[0] !== '7' && numericValue[0] !== '8') {
      numericValue = '7' + numericValue;
    }

    formattedValue += numericValue.substring(1, 4);

    if (numericValue.length >= 5) {
      formattedValue += ') ' + numericValue.substring(4, 7);
    }

    if (numericValue.length >= 8) {
      formattedValue += '-' + numericValue.substring(7, 9);
    }

    if (numericValue.length >= 10) {
      formattedValue += '-' + numericValue.substring(9, 11);
    }

    model.value = formattedValue;
  }

  emit('onInput', model.value);
};

const onChange = () => {
  emit('onChange', model.value);
};

const onEnter = () => {
  inputRef.value?.blur();
  onChange();
  emit('onEnter', model.value);
};

const onFocus = () => {
  if (mask === 'phone') {
    const input = inputRef.value as HTMLInputElement;

    if (!input.value) {
      input.value = '+7 (';
    }
  }
  emit('onFocus', model.value);
};

const onTab = () => {
  emit('onTab', model.value);
};

const onBlur = () => {
  emit('onBlur', model.value);
};

const onClear = () => {
  clear();
  nextTick(() => {
    onChange();
    inputRef.value?.focus();
  });
};

onMounted(() => {
  if (focus) {
    inputRef.value?.focus();
  }
});
</script>

<template>
  <div :class="className">
    <label
      v-if="label"
      :for="uid"
      :class="`${className}__label`"
    >
      {{ label }}
      <span
        v-if="required"
        class="required"
      >*</span>
    </label>
    <p
      v-if="description"
      :class="`${className}__description`"
    >
      {{ description }}
    </p>
    <div :class="[`${className}__box`, inputClass, { errored: error, disabled: disabled, suffix: suffix || prefix }]">
      <div
        v-if="prefix"
        :class="`${className}__prefix`"
      >
        {{ prefix }}
      </div>
      <UiIcon
        v-if="icon"
        :name="icon"
        :class="`${className}__icon`"
      />
      <input
        :id="uid"
        ref="inputRef"
        v-model="model"
        :name="name"
        :type="type === 'text' || isShowPassword ? 'text' : 'password'"
        :placeholder="placeholder"
        :class="`${className}__input`"
        :disabled="disabled"
        :readonly="readonly"
        @keydown.enter.stop.prevent="onEnter"
        @keyup.esc="onClear"
        @keyup.tab="onTab"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      >
      <UiBtn
        v-if="type === 'password'"
        type="icon"
        :icon="isShowPassword ? 'eye-closed' : 'eye-opened'"
        tabindex="-1"
        :class="`${className}__action`"
        @click.stop.prevent="isShowPassword = !isShowPassword"
        @mousedown.stop.prevent
      />
      <UiBtn
        v-else
        type="icon"
        icon="close"
        tabindex="-1"
        :class="`${className}__action`"
        @click.stop.prevent="onClear"
        @mousedown.stop.prevent
      />
      <div
        v-if="suffix"
        :class="`${className}__suffix`"
      >
        {{ suffix }}
      </div>
    </div>
    <p
      v-if="maxLength"
      :class="[
        `${className}__message`,
        {
          [`${className}__message_info`]: model?.length !== maxLength,
          [`${className}__message_error`]: model?.length === maxLength,
        },
      ]"
    >
      Кол-во знаков: {{ model?.length }} из {{ maxLength }} допустимых
    </p>
    <p
      v-if="info"
      :class="`${className}__message ${className}__message_info`"
    >
      {{ info }}
    </p>
    <p
      v-if="error"
      :class="`${className}__message ${className}__message_error`"
    >
      {{ error }}
    </p>
    <p
      v-if="success"
      :class="`${className}__message ${className}__message_success`"
    >
      {{ success }}
    </p>
  </div>
</template>
